import React, { useState, useEffect } from 'react';
import { apiGet, apiPost } from '../../../services/apiServices';
import { endPoints } from '../../../configs/apiUrls';
import { toast } from 'react-toastify';
import { FaSave, FaSpinner, FaPlus } from 'react-icons/fa';

// 🚨 Updated to match new clean backend schema
type TierRates = {
  tier1Rate: number; 
  tier2Rate: number; 
  tier3Rate: number;
};

type Option = TierRates & { _id?: string; label: string; };
type BaseParameter = { _id?: string; categoryName: string; options: Option[]; };
type Amenity = TierRates & { _id?: string; name: string; unitType: string; };

type MasterData = {
  propertyType: string;
  rateType: string;
  depreciationPercentagePerYear: number;
  baseParameters: BaseParameter[];
  amenities: Amenity[];
};

const TABS = ['Building', 'Commercial', 'Apartment'];

// Strict filters for UI display
const GOV_ALLOWED_PARAMS = ["structure type", "type of flooring"];
const GOV_ALLOWED_AMENITIES = ["car parking"];

const ConstructionMasterDashboard = () => {
  const [activeTab, setActiveTab] = useState<string>('Building');
  const [rateMode, setRateMode] = useState<'Market' | 'Government'>('Market');
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [masterData, setMasterData] = useState<MasterData | null>(null);

  // 🚨 Updated fetch to include rateType dynamically
  const fetchMasterData = async (type: string, mode: string) => {
    setLoading(true);
    try {
      const res: any = await apiGet(`${endPoints.GET_CONSTRUCTION_MASTER}/${type}?rateType=${mode.toLowerCase()}`);
      if (res?.success && res.data) {
        setMasterData(res.data);
      } else {
        setMasterData({
          propertyType: type, 
          rateType: mode.toLowerCase(),
          depreciationPercentagePerYear: 1, 
          baseParameters: [], 
          amenities: []
        });
      }
    } catch (error) {
      toast.error("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  // 🚨 Re-fetch data whenever Tab OR Rate Mode changes
  useEffect(() => { 
    fetchMasterData(activeTab, rateMode); 
  }, [activeTab, rateMode]);

  const handleParameterChange = (paramIndex: number, optionIndex: number, tier: 1 | 2 | 3, value: string) => {
    if (!masterData) return;
    const newData = { ...masterData };
    const field = `tier${tier}Rate` as keyof TierRates;
    newData.baseParameters[paramIndex].options[optionIndex][field] = Number(value) || 0;
    setMasterData(newData);
  };

  const handleAmenityChange = (amenityIndex: number, tier: 1 | 2 | 3, value: string) => {
    if (!masterData) return;
    const newData = { ...masterData };
    const field = `tier${tier}Rate` as keyof TierRates;
    newData.amenities[amenityIndex][field] = Number(value) || 0;
    setMasterData(newData);
  };

  const addNewAmenity = () => {
    if (!masterData) return;
    const newData = { ...masterData };
    newData.amenities.push({
      name: 'Car Parking', unitType: "No's",
      tier1Rate: 0, tier2Rate: 0, tier3Rate: 0,
    });
    setMasterData(newData);
  };

  const handleSave = async () => {
    if (!masterData) return;
    setSaving(true);
    try {
      // Ensure rateType is strictly set before saving
      const payload = { ...masterData, rateType: rateMode.toLowerCase() };
      const res: any = await apiPost(endPoints.UPDATE_CONSTRUCTION_MASTER, payload);
      
      if (res?.success) toast.success(`${activeTab} (${rateMode}) configuration saved successfully!`);
      else toast.error(res?.message || "Failed to save");
    } catch (error) {
      toast.error("Something went wrong while saving");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 pb-20">
      <div className="flex bg-white border-b border-gray-200 px-6 pt-4 space-x-8 sticky top-0 z-10 shadow-sm justify-between items-center">
        <div className="flex space-x-8">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 text-sm font-semibold border-b-2 ${activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              {tab} Rates
            </button>
          ))}
        </div>

        <div className="pb-3 flex space-x-2 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setRateMode('Market')}
            className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${rateMode === 'Market' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Market Rates
          </button>
          <button
            onClick={() => setRateMode('Government')}
            className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${rateMode === 'Government' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Government Rates
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex h-64 items-center justify-center"><FaSpinner className="animate-spin text-blue-600" size={30} /></div>
        ) : masterData ? (
          <div className="max-w-6xl mx-auto space-y-8">

            {rateMode === 'Market' ? (
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Depreciation Settings</h3>
                  <p className="text-sm text-gray-500">Applied per year to the base building value.</p>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    className="w-24 text-right rounded-lg border-gray-300 focus:border-blue-500"
                    value={masterData.depreciationPercentagePerYear}
                    onChange={(e) => setMasterData({ ...masterData, depreciationPercentagePerYear: Number(e.target.value) || 0 })}
                  />
                  <span className="font-semibold text-gray-600">%</span>
                </div>
              </div>
            ) : (
              <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                <p className="text-green-800 font-semibold text-sm">ℹ️ Government rates are restricted to Structure Type, Flooring, and Car Parking. No depreciation is applied.</p>
              </div>
            )}

            {/* Base Parameters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className={`px-6 py-4 ${rateMode === 'Market' ? 'bg-gray-800' : 'bg-green-800'}`}>
                <h3 className="text-lg font-bold text-white">{rateMode} Base Parameter Rates (₹ / Sq.Ft)</h3>
              </div>

              {masterData.baseParameters?.map((param, paramIdx) => {
                // 🚨 Hide irrelevant parameters in Government Mode
                if (rateMode === 'Government' && !GOV_ALLOWED_PARAMS.includes(param.categoryName.toLowerCase())) {
                  return null;
                }

                return (
                  <div key={paramIdx} className="border-b border-gray-200 last:border-b-0">
                    <div className="bg-gray-100 px-6 py-2 border-b flex items-center justify-between">
                      <span className="font-semibold text-gray-700">{paramIdx + 1}. {param.categoryName}</span>
                    </div>

                    <div className="px-6 py-4 overflow-x-auto">
                      <table className="min-w-full text-sm text-left">
                        <thead>
                          <tr className="text-gray-500">
                            <th className="pb-3 font-medium w-1/3">Option</th>
                            <th className="pb-3 font-medium w-1/5 text-center">Tier 1 (Metro/Corp)</th>
                            <th className="pb-3 font-medium w-1/5 text-center">Tier 2 (Town/Panchayath)</th>
                            <th className="pb-3 font-medium w-1/5 text-center">Tier 3 (Grama)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {param.options.map((opt, optIdx) => (
                            <tr key={optIdx} className="border-t border-gray-100">
                              <td className="py-2 font-medium text-gray-800">{opt.label}</td>
                              {[1, 2, 3].map((tier) => (
                                <td key={tier} className="py-2 px-2">
                                  <input
                                    type="number"
                                    className={`w-full text-center rounded border-gray-300 py-1.5 text-sm ${rateMode === 'Government' ? 'focus:ring-green-500' : 'focus:ring-blue-500'}`}
                                    value={opt[`tier${tier}Rate` as keyof TierRates]}
                                    onChange={(e) => handleParameterChange(paramIdx, optIdx, tier as 1 | 2 | 3, e.target.value)}
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Amenities */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className={`px-6 py-4 flex justify-between items-center ${rateMode === 'Market' ? 'bg-blue-800' : 'bg-teal-800'}`}>
                <h3 className="text-lg font-bold text-white">{rateMode} Amenities & Extras (Flat ₹)</h3>

                {/* 🚨 Hide Add Button in Government Mode */}
                {/* {rateMode === 'Market' && (
                  <button onClick={addNewAmenity} className="flex items-center text-sm bg-white text-gray-800 px-3 py-1 rounded hover:bg-gray-100">
                    <FaPlus className="mr-2 text-xs" /> Add Amenity
                  </button>
                )} */}
              </div>

              <div className="px-6 py-4 overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-200">
                      <th className="pb-3 font-medium w-1/4">Amenity Name</th>
                      <th className="pb-3 font-medium w-1/6 text-center">Unit Type</th>
                      <th className="pb-3 font-medium w-1/5 text-center">Tier 1 (Metro/Corp)</th>
                      <th className="pb-3 font-medium w-1/5 text-center">Tier 2 (Town/Panchayath)</th>
                      <th className="pb-3 font-medium w-1/5 text-center">Tier 3 (Grama)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {masterData.amenities?.map((amenity, amIdx) => {
                      // 🚨 Strictly show ONLY Car Parking in Government Mode
                      if (rateMode === 'Government' && amenity.name.toLowerCase() !== "car parking") {
                        return null;
                      }

                      return (
                        <tr key={amIdx} className="border-b border-gray-100 last:border-0">
                          <td className="py-3">
                            <input
                              type="text"
                              // 🚨 Disable editing the name in Gov mode so they don't break the filter
                              disabled={rateMode === 'Government'}
                              className={`w-full text-sm rounded border-gray-300 ${rateMode === 'Government' ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                              value={amenity.name}
                              onChange={(e) => {
                                const newData = { ...masterData };
                                newData.amenities[amIdx].name = e.target.value;
                                setMasterData(newData);
                              }}
                            />
                          </td>
                          <td className="py-3 px-2">
                            <input
                              type="text"
                              disabled={rateMode === 'Government'}
                              className={`w-full text-center text-sm rounded border-gray-300 ${rateMode === 'Government' ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                              value={amenity.unitType}
                              onChange={(e) => {
                                const newData = { ...masterData };
                                newData.amenities[amIdx].unitType = e.target.value;
                                setMasterData(newData);
                              }}
                            />
                          </td>
                          {[1, 2, 3].map((tier) => (
                            <td key={tier} className="py-2 px-2">
                              <input
                                type="number"
                                className={`w-full text-center rounded border-gray-300 py-1.5 text-sm ${rateMode === 'Government' ? 'focus:ring-teal-500' : 'focus:ring-blue-500'}`}
                                value={amenity[`tier${tier}Rate` as keyof TierRates]}
                                onChange={(e) => handleAmenityChange(amIdx, tier as 1 | 2 | 3, e.target.value)}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        ) : null}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 shadow-lg flex justify-end z-20">
        <div className="max-w-6xl w-full mx-auto flex justify-end pr-6">
          <button
            onClick={handleSave} disabled={saving || !masterData}
            className="flex items-center px-8 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? <FaSpinner className="animate-spin mr-2" /> : <FaSave className="mr-2" />}
            {saving ? 'Saving...' : `Save Configuration`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConstructionMasterDashboard;